/**
 * shop.js — collection (/shop) + search slice behaviors.
 *
 * Ports:
 *  - components/layout/search/filter/dropdown.tsx -> generic [data-dropdown]
 *    controller (mobile sort dropdown inside the FilterList nav): toggle on
 *    click, close on outside click / Escape / option selection.
 *  - Next.js route-transition feedback for filter & sort links: the app
 *    re-rendered the grid through React Suspense; here any [data-filter-link]
 *    click dims [data-product-grid] until the new document loads.
 *
 * Markup contracts (emitted by sections/main-collection.liquid):
 *   <div data-dropdown>
 *     <div data-dropdown-toggle>…</div>
 *     <div data-dropdown-menu hidden>…</div>
 *   </div>
 *   <a data-filter-link href="…">  — category/tag/price/sort/pagination link
 *   <div data-product-grid>        — the products grid
 */
(function () {
  "use strict";

  // Per-element binding; scoped to `root` and idempotent (the __dropdownInit
  // guard) so it can be safely re-run for a single section re-rendered in the
  // theme editor without double-binding existing dropdowns.
  function initDropdowns(root) {
    (root || document)
      .querySelectorAll("[data-dropdown]")
      .forEach(function (el) {
        if (el.__dropdownInit) return;
        var toggle = el.querySelector("[data-dropdown-toggle]");
        var menu = el.querySelector("[data-dropdown-menu]");
        if (!toggle || !menu) return;
        el.__dropdownInit = true;

        toggle.setAttribute("role", "button");
        toggle.setAttribute("tabindex", "0");
        toggle.setAttribute("aria-expanded", "false");

        function setOpen(open) {
          menu.hidden = !open;
          toggle.setAttribute("aria-expanded", open ? "true" : "false");
        }

        toggle.addEventListener("click", function (e) {
          e.stopPropagation();
          setOpen(menu.hidden);
        });
        toggle.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(menu.hidden);
          }
        });
        // Selecting an option (a navigation) closes the menu.
        menu.addEventListener("click", function () {
          setOpen(false);
        });
        el.setOpen = setOpen;
      });
  }

  // Document-level dismiss listeners — bound once, survive section re-renders.
  function initDropdownDismiss() {
    // Close on outside click (port of the window click-outside listener).
    document.addEventListener("click", function (e) {
      document.querySelectorAll("[data-dropdown]").forEach(function (el) {
        var menu = el.querySelector("[data-dropdown-menu]");
        if (!menu || menu.hidden) return;
        if (el.setOpen && !el.contains(e.target)) el.setOpen(false);
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      document.querySelectorAll("[data-dropdown]").forEach(function (el) {
        if (el.setOpen) el.setOpen(false);
      });
    });
  }

  function initFilterLinkFeedback() {
    document.addEventListener("click", function (e) {
      var link = e.target.closest("[data-filter-link]");
      if (!link) return;
      // Plain left-clicks only — let modified clicks open new tabs untouched.
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var grid = document.querySelector("[data-product-grid]");
      if (grid) {
        grid.style.transition = "opacity 0.2s ease";
        grid.style.opacity = "0.5";
      }
    });
    // Restore when the page is shown again from bfcache (back button).
    window.addEventListener("pageshow", function () {
      var grid = document.querySelector("[data-product-grid]");
      if (grid) grid.style.opacity = "";
    });
  }

  function init() {
    initDropdowns();
    initDropdownDismiss();
    initFilterLinkFeedback();
  }

  if (window.themeUtils && window.themeUtils.onReady) {
    window.themeUtils.onReady(init);
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Theme editor: re-bind dropdowns for a re-rendered section (called by
  // theme.js on shopify:section:load). Idempotent via __dropdownInit.
  window.themeShop = { scan: initDropdowns };
})();
